import React, { useState, useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import type { Recipe } from "../../types";
import type { ExtractedRecipe } from "../../types/recipe-import";
import {
  convertExtractedToRecipe,
  validateExtractedRecipe,
} from "../../util/recipe-import-helper";
import IngredientList from "../ingredient/ingredient-list";
import RecipeMethod from "../recipe/recipe-method";
import LabelList from "../label/label-list";

const useStyles = makeStyles()((theme) => ({
  section: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    marginBottom: theme.spacing(1),
    fontWeight: 600,
  },
}));

interface RecipePreviewDialogProps {
  open: boolean;
  handleClose: () => void;
  extractedRecipe: ExtractedRecipe | null;
  onAccept: (recipe: Recipe) => void;
  onReject: () => void;
}

export default function RecipePreviewDialog(props: RecipePreviewDialogProps) {
  const { classes } = useStyles();
  const [recipe, setRecipe] = useState<Recipe | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);

  useEffect(() => {
    if (props.extractedRecipe) {
      try {
        const { recipe: converted, warnings: conversionWarnings } = convertExtractedToRecipe(props.extractedRecipe);
        setRecipe(converted);
        setWarnings(conversionWarnings);
        setValidationErrors([]);
      } catch (error: any) {
        setValidationErrors([error.message || 'Failed to convert recipe data']);
        setRecipe(null);
      }
    }
  }, [props.extractedRecipe]);

  const handleAccept = () => {
    if (!recipe) return;

    const validation = validateExtractedRecipe(recipe);
    if (!validation.isValid) {
      setValidationErrors(validation.errors);
      return;
    }

    props.onAccept(recipe);
    setValidationErrors([]);
    setWarnings([]);
    setRecipe(null);
  };

  const handleReject = () => {
    setValidationErrors([]);
    setRecipe(null);
    props.onReject();
  };

  const handleClose = () => {
    setValidationErrors([]);
    setRecipe(null);
    props.handleClose();
  };

  if (!recipe) return null;

  return (
    <Dialog
      open={props.open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <DialogTitle>Preview Imported Recipe</DialogTitle>
      <DialogContent dividers>
        {validationErrors.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <strong>Please fix the following errors:</strong>
            <ul style={{ margin: "8px 0 0 0", paddingLeft: "20px" }}>
              {validationErrors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </Alert>
        )}

        {warnings.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>Warnings:</strong>
            <ul style={{ margin: "8px 0 0 0", paddingLeft: "20px" }}>
              {warnings.map((warning, index) => (
                <li key={index}>{warning}</li>
              ))}
            </ul>
          </Alert>
        )}

        {/* Recipe Name */}
        <Box className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Recipe Name
          </Typography>
          <TextField
            fullWidth
            value={recipe.name}
            onChange={(e) => setRecipe({ ...recipe, name: e.target.value })}
            error={!recipe.name?.trim()}
            helperText={!recipe.name?.trim() ? "Name is required" : ""}
          />
        </Box>

        {/* Serves */}
        <Box className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Serves
          </Typography>
          <TextField
            type="number"
            value={recipe.serves}
            onChange={(e) =>
              setRecipe({ ...recipe, serves: parseInt(e.target.value) || 0 })
            }
            inputProps={{ min: 1 }}
            error={!recipe.serves || recipe.serves <= 0}
            helperText={
              !recipe.serves || recipe.serves <= 0 ? "Must be at least 1" : ""
            }
            sx={{ width: "150px" }}
          />
        </Box>

        {/* Calories */}
        <Box className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Calories (per serving)
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TextField
              type="number"
              value={recipe.calories ?? ""}
              onChange={(e) =>
                setRecipe({
                  ...recipe,
                  calories: e.target.value
                    ? parseInt(e.target.value, 10)
                    : undefined,
                  caloriesEstimated: false,
                })
              }
              inputProps={{ min: 0 }}
              placeholder="Not specified"
              sx={{ width: "150px" }}
            />
            {recipe.caloriesEstimated && (
              <Chip label="Estimated" size="small" color="info" />
            )}
          </Box>
        </Box>

        {/* Labels */}
        <Box className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Labels
          </Typography>
          <LabelList recipe={recipe} setRecipe={setRecipe} editMode={true} />
        </Box>

        {/* Ingredients */}
        <Box className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Ingredients
          </Typography>
          <IngredientList
            ingredients={recipe.ingredients}
            setIngredients={(newIngredients: any) =>
              setRecipe({ ...recipe, ingredients: newIngredients })
            }
            editMode={true}
          />
        </Box>

        {/* Method */}
        <Box className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Method
          </Typography>
          <RecipeMethod
            method={recipe.method}
            setMethod={(newMethod: any) =>
              setRecipe({ ...recipe, method: newMethod })
            }
            editMode={true}
          />
        </Box>

        {/* Notes */}
        <Box className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Notes
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={recipe.notes || ""}
            onChange={(e) => setRecipe({ ...recipe, notes: e.target.value })}
            placeholder="Additional notes, tips, or cooking time..."
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleReject}>Cancel</Button>
        <Button onClick={handleAccept} variant="contained" color="primary">
          Use This Recipe
        </Button>
      </DialogActions>
    </Dialog>
  );
}
